import { __ } from '@wordpress/i18n';

const REST_BASE = '/wp-json/anacrm/v1';

function nonce() {
	return window.anacrm?.nonce ?? '';
}

async function apiFetch( url, options = {} ) {
	const res = await fetch( url, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': nonce(),
			...( options.headers ?? {} ),
		},
	} );
	if ( res.status === 204 ) {
		return null;
	}
	const body = await res.json().catch( () => ( {} ) );
	if ( ! res.ok ) {
		// eslint-disable-next-line no-console
		console.error( '[product] apiFetch error', res.status, url, body );
		throw new Error( body?.message ?? `HTTP ${ res.status }` );
	}
	return body;
}

/**
 * Reads the currently selected term IDs from the org form's `_or_field`
 * Pods multi-autocomplete widget, live from the DOM. Same technique as
 * getSelectedFieldIds() in solvencyRepeater.js.
 * @param {HTMLElement|null} orgFieldWrapper
 */
function getSelectedFieldIds( orgFieldWrapper ) {
	if ( ! orgFieldWrapper ) {
		return [];
	}
	return [
		...orgFieldWrapper.querySelectorAll(
			'input[type="hidden"][name^="pods_field__or_field"]'
		),
	]
		.map( ( el ) => parseInt( el.value, 10 ) )
		.filter( ( n ) => ! isNaN( n ) );
}

async function fetchFieldTerms( ids ) {
	if ( ! ids.length ) {
		return [];
	}
	const res = await fetch(
		`/wp-json/wp/v2/field?include=${ ids.join(
			','
		) }&_fields=id,name&per_page=100`,
		{ headers: { 'X-WP-Nonce': nonce() } }
	);
	if ( ! res.ok ) {
		return [];
	}
	return res.json();
}

// GET responses use camelCase ids; pending (not-yet-persisted) entries use
// the snake_case REST payload keys — normalise reads across both shapes.
const extractFieldId = ( item ) => item.field_id ?? item.fieldId ?? null;

function productLabel( item ) {
	const desc = ( item.description ?? '' ).slice( 0, 80 );
	return `${ item.fieldName || __( '(no category)', 'anacrm' ) } — ${ desc }`;
}

function renderRow( container, item, onDelete, onEdit ) {
	const row = document.createElement( 'div' );
	row.className = 'flex items-center gap-2 py-1 product-row';
	row.dataset.id = item.id ?? '';

	const label = document.createElement( 'span' );
	label.className = 'flex-1 text-sm';
	label.textContent = productLabel( item );

	const edit = document.createElement( 'button' );
	edit.type = 'button';
	edit.className =
		'px-2 py-0.5 text-sm text-gray-600 hover:text-gray-900 cursor-pointer shrink-0';
	edit.textContent = __( 'Edit', 'anacrm' );
	edit.addEventListener( 'click', () => onEdit( row, label, item ) );

	const del = document.createElement( 'button' );
	del.type = 'button';
	del.className =
		'px-2 py-0.5 text-sm text-red-600 hover:text-red-800 cursor-pointer shrink-0';
	del.textContent = __( 'Remove', 'anacrm' );
	del.addEventListener( 'click', () => onDelete( row, item ) );

	row.appendChild( label );
	row.appendChild( edit );
	row.appendChild( del );
	container.appendChild( row );
}

// Builds one "Products" or "Services" subsection (add/edit/delete flow),
// shared between both since they only differ in `_pd_type` and copy. Both
// require a category, restricted to the org's own `_or_field` selections —
// unlike solvencyRepeater.js, where only "technical" does.
function buildSubsection( {
	container,
	type,
	heading,
	addLabel,
	warningText,
	errorText,
	items,
	orgId,
	isNew,
	orgFieldWrapper,
	pending,
	syncHidden,
	handleDelete,
	handleDeletePending,
} ) {
	const wrap = document.createElement( 'div' );
	wrap.className = 'mb-6';
	container.appendChild( wrap );

	const label = document.createElement( 'p' );
	label.className = 'text-sm font-medium mb-1';
	label.textContent = heading;
	wrap.appendChild( label );

	const list = document.createElement( 'div' );
	list.className = 'product-list mb-3 space-y-1';
	wrap.appendChild( list );

	for ( const item of items.filter( ( i ) => type === i.type ) ) {
		renderRow( list, item, handleDelete, startEdit );
	}

	const addArea = document.createElement( 'div' );
	wrap.appendChild( addArea );

	const addBtn = document.createElement( 'button' );
	addBtn.type = 'button';
	addBtn.className =
		'text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer';
	addBtn.textContent = addLabel;
	addArea.appendChild( addBtn );

	const warning = document.createElement( 'p' );
	warning.className = 'hidden text-xs text-red-600 mt-2';
	warning.textContent = warningText;
	addArea.appendChild( warning );

	const form = document.createElement( 'div' );
	form.className =
		'hidden flex flex-wrap gap-2 mt-2 items-end product-add-form';
	addArea.appendChild( form );

	const category = document.createElement( 'select' );
	category.className = 'border border-gray-300 rounded px-2 py-1 text-sm';

	const description = document.createElement( 'textarea' );
	description.rows = 2;
	description.maxLength = 255;
	description.placeholder = __( 'Description', 'anacrm' );
	description.className =
		'border border-gray-300 rounded px-2 py-1 text-sm flex-1 min-w-[200px]';

	const save = document.createElement( 'button' );
	save.type = 'button';
	save.className =
		'px-3 py-1 text-sm bg-gray-800 text-white rounded hover:bg-gray-700 cursor-pointer shrink-0';
	save.textContent = __( 'Save', 'anacrm' );

	const cancel = document.createElement( 'button' );
	cancel.type = 'button';
	cancel.className =
		'px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 cursor-pointer shrink-0';
	cancel.textContent = __( 'Cancel', 'anacrm' );

	const err = document.createElement( 'p' );
	err.className = 'w-full text-xs text-red-600 hidden';

	form.appendChild( category );
	form.appendChild( description );
	form.appendChild( save );
	form.appendChild( cancel );
	form.appendChild( err );

	let editing = null;

	function populateCategorySelect( terms, selectedId ) {
		category.innerHTML = '';
		for ( const t of terms ) {
			const opt = document.createElement( 'option' );
			opt.value = String( t.id );
			opt.textContent = t.name;
			if ( selectedId && t.id === selectedId ) {
				opt.selected = true;
			}
			category.appendChild( opt );
		}
	}

	function setFormEnabled( enabled ) {
		category.disabled = ! enabled;
		description.disabled = ! enabled;
		save.disabled = ! enabled;
	}

	function startEdit( row, rowLabel, item ) {
		editing = { row, label: rowLabel, item };
		addBtn.classList.add( 'hidden' );
		warning.classList.add( 'hidden' );
		form.classList.remove( 'hidden' );
		description.value = item.description ?? '';
		err.classList.add( 'hidden' );
		save.textContent = __( 'Update', 'anacrm' );

		const currentFieldId = extractFieldId( item );
		const ids = getSelectedFieldIds( orgFieldWrapper );

		populateCategorySelect(
			currentFieldId
				? [ { id: currentFieldId, name: item.fieldName ?? '' } ]
				: [],
			currentFieldId
		);
		setFormEnabled( false );

		fetchFieldTerms( ids ).then( ( terms ) => {
			// Keep the row's own category selectable even if it's no longer
			// among the org's currently selected `_or_field` terms.
			const merged =
				currentFieldId &&
				! terms.some( ( t ) => t.id === currentFieldId )
					? [
							...terms,
							{ id: currentFieldId, name: item.fieldName ?? '' },
					  ]
					: terms;
			populateCategorySelect( merged, currentFieldId );
			setFormEnabled( true );
		} );
	}

	function stopEdit() {
		editing = null;
		description.value = '';
		form.classList.add( 'hidden' );
		warning.classList.add( 'hidden' );
		addBtn.classList.remove( 'hidden' );
		save.textContent = __( 'Save', 'anacrm' );
	}

	addBtn.addEventListener( 'click', async () => {
		addBtn.classList.add( 'hidden' );
		form.classList.remove( 'hidden' );
		description.value = '';
		err.classList.add( 'hidden' );

		const ids = getSelectedFieldIds( orgFieldWrapper );

		if ( ! ids.length ) {
			warning.classList.remove( 'hidden' );
			populateCategorySelect( [], null );
			setFormEnabled( false );
			return;
		}

		warning.classList.add( 'hidden' );
		setFormEnabled( false );

		const terms = await fetchFieldTerms( ids );

		if ( ! terms.length ) {
			warning.classList.remove( 'hidden' );
			populateCategorySelect( [], null );
			return;
		}

		populateCategorySelect( terms, null );
		setFormEnabled( true );
	} );

	cancel.addEventListener( 'click', stopEdit );

	save.addEventListener( 'click', async () => {
		const fieldId = parseInt( category.value, 10 );
		const desc = description.value.trim();
		const fieldName =
			category.options[ category.selectedIndex ]?.textContent ?? '';

		if ( ! fieldId || ! desc || desc.length > 255 ) {
			err.textContent = errorText;
			err.classList.remove( 'hidden' );
			return;
		}

		err.classList.add( 'hidden' );
		save.disabled = true;

		if ( editing ) {
			const { label: rowLabel, item } = editing;
			if ( item.id ) {
				try {
					const updated = await apiFetch(
						`${ REST_BASE }/products/${ item.id }`,
						{
							method: 'PUT',
							body: JSON.stringify( {
								org_id: parseInt( orgId, 10 ),
								type,
								field_id: fieldId,
								description: desc,
							} ),
						}
					);
					Object.assign( item, updated );
					rowLabel.textContent = productLabel( item );
					stopEdit();
				} catch ( saveErr ) {
					// eslint-disable-next-line no-console
					console.error( '[product] update failed', saveErr );
					err.textContent =
						saveErr.message ||
						__( 'Could not save. Please try again.', 'anacrm' );
					err.classList.remove( 'hidden' );
				} finally {
					save.disabled = false;
				}
			} else {
				item.field_id = fieldId;
				item.fieldName = fieldName;
				item.description = desc;
				syncHidden();
				rowLabel.textContent = productLabel( item );
				stopEdit();
				save.disabled = false;
			}
			return;
		}

		if ( isNew ) {
			const entry = {
				type,
				field_id: fieldId,
				fieldName,
				description: desc,
			};
			pending.push( entry );
			syncHidden();
			renderRow( list, entry, handleDeletePending, startEdit );
			stopEdit();
			save.disabled = false;
		} else {
			try {
				const created = await apiFetch( `${ REST_BASE }/products`, {
					method: 'POST',
					body: JSON.stringify( {
						org_id: parseInt( orgId, 10 ),
						type,
						field_id: fieldId,
						description: desc,
					} ),
				} );
				renderRow( list, created, handleDelete, startEdit );
				stopEdit();
			} catch ( saveErr ) {
				// eslint-disable-next-line no-console
				console.error( '[product] save failed', saveErr );
				err.textContent =
					saveErr.message ||
					__( 'Could not save. Please try again.', 'anacrm' );
				err.classList.remove( 'hidden' );
			} finally {
				save.disabled = false;
			}
		}
	} );
}

/**
 * Mount the "Products and services" repeater into `container`. Two
 * subsections — Products (`_pd_type = 'product'`) and Services
 * (`_pd_type = 'service'`) — each with its own add/edit/delete flow, both
 * requiring a category restricted to the org's own `_or_field` selections.
 * Follows the same isNew/pending-hidden-input pattern as
 * initOrgSolvencySection() in solvencyRepeater.js.
 *
 * @param {HTMLElement}      container       Mount point (inside the org form).
 * @param {string}           orgId           Post ID string, empty for new-org form.
 * @param {boolean}          isNew           True when creating a new org.
 * @param {HTMLElement|null} orgFieldWrapper The `_or_field` Pods field wrapper.
 */
export async function initOrgProductSection(
	container,
	orgId,
	isNew,
	orgFieldWrapper
) {
	const intro = document.createElement( 'p' );
	intro.className = 'text-sm mb-3';
	intro.textContent = __(
		'List the products and services offered by the organization, each linked to one of its categories selected above.',
		'anacrm'
	);
	container.appendChild( intro );

	// Pending list for new-org form (not yet persisted). Shared between the
	// products and services subsections, each entry tagged by `type`.
	let pending = [];

	const hiddenInput = document.createElement( 'input' );
	hiddenInput.type = 'hidden';
	hiddenInput.name = 'anacrm_pending_products';
	hiddenInput.value = '[]';
	container.appendChild( hiddenInput );

	function syncHidden() {
		hiddenInput.value = JSON.stringify( pending );
	}

	let items = [];
	if ( ! isNew && orgId ) {
		try {
			const fetched = await apiFetch(
				`${ REST_BASE }/org/${ orgId }/products`
			);
			if ( Array.isArray( fetched ) ) {
				items = fetched;
			}
		} catch {
			// Non-fatal — show empty lists.
		}
	}

	async function handleDelete( row, item ) {
		if ( ! item.id ) {
			return;
		}
		try {
			await apiFetch( `${ REST_BASE }/products/${ item.id }`, {
				method: 'DELETE',
			} );
			row.remove();
		} catch {
			const e = document.createElement( 'span' );
			e.className = 'text-xs text-red-600 ml-1';
			e.textContent = __( 'Error', 'anacrm' );
			row.appendChild( e );
		}
	}

	function handleDeletePending( row, item ) {
		pending = pending.filter( ( p ) => p !== item );
		syncHidden();
		row.remove();
	}

	const shared = {
		orgId,
		isNew,
		orgFieldWrapper,
		items,
		pending,
		syncHidden,
		handleDelete,
		handleDeletePending,
	};

	buildSubsection( {
		...shared,
		container,
		type: 'product',
		heading: __( 'Products offered by the organization', 'anacrm' ),
		addLabel: __( '+ Add product', 'anacrm' ),
		warningText: __(
			'Select at least one category in "Category" above before adding a product.',
			'anacrm'
		),
		errorText: __(
			'Please select a category and enter a description.',
			'anacrm'
		),
	} );

	buildSubsection( {
		...shared,
		container,
		type: 'service',
		heading: __( 'Services offered by the organization', 'anacrm' ),
		addLabel: __( '+ Add service', 'anacrm' ),
		warningText: __(
			'Select at least one category in "Category" above before adding a service.',
			'anacrm'
		),
		errorText: __(
			'Please select a category and enter a description.',
			'anacrm'
		),
	} );
}
